import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, User, MapPin, Phone, Mail, Calendar, FileText, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'bozza', label: 'Bozza' },
  { value: 'attivo', label: 'Attivo' },
  { value: 'sospeso', label: 'Sospeso' },
  { value: 'da_completare', label: 'Da Completare' },
  { value: 'cessato', label: 'Cessato' }
];

export default function VolontarioForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    codice_fiscale: '',
    data_nascita: '',
    luogo_nascita: '',
    indirizzo: '',
    telefono: '',
    email: '',
    pec: '',
    contatto_emergenza: '',
    data_inizio_attivita: '',
    data_cessazione_attivita: '',
    qualifica: '',
    competenze: '',
    disponibilita: '',
    note: '',
    consenso_privacy: false,
    stato: 'bozza',
    // Documento di identità
    tipo_documento_identita: '',
    numero_documento_identita: '',
    documento_rilasciato_da: '',
    documento_data_rilascio: '',
    documento_data_scadenza: '',
    // Classificazione volontario
    tipologia_volontario: 'non_occasionale',
    // Campi non occasionale
    data_inizio_continuativa: '',
    data_cessazione_continuativa: '',
    presenza_registro_vidimato: 'da_verificare',
    data_ultima_verifica_registro: '',
    note_amministrative: '',
    // Campi occasionale
    evento_riferimento_id: '',
    periodo_attivita_occasionale: '',
    referente_occasionale: '',
    note_occasionale: '',
    // Motivazione modifica
    motivazione_modifica: ''
  });
  const [eventi, setEventi] = useState([]);

  useEffect(() => {
    if (isEdit) {
      fetchVolontario();
    }
    fetchEventi();
  }, [id]);

  const fetchEventi = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eventi`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEventi(data);
      }
    } catch (error) {
      console.error('Error loading eventi:', error);
    }
  };

  const fetchVolontario = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/volontari/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
      } else {
        toast.error('Volontario non trovato');
        navigate('/volontari');
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
      navigate('/volontari');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione campi obbligatori
    if (!formData.nome || formData.nome.trim() === '') {
      toast.error('Il Nome è obbligatorio');
      return;
    }
    if (!formData.cognome || formData.cognome.trim() === '') {
      toast.error('Il Cognome è obbligatorio');
      return;
    }
    // Codice fiscale opzionale ma se presente deve essere di 16 caratteri
    if (formData.codice_fiscale && formData.codice_fiscale.length !== 16) {
      toast.error('Il Codice Fiscale deve essere di 16 caratteri');
      return;
    }

    setSaving(true);
    try {
      const url = isEdit 
        ? `${API_URL}/api/volontari/${id}`
        : `${API_URL}/api/volontari`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        const timestamp = new Date().toLocaleString('it-IT');
        toast.success(
          isEdit 
            ? `Volontario aggiornato correttamente il ${timestamp}`
            : `Volontario creato correttamente il ${timestamp}`
        );
        navigate('/volontari');
      } else {
        const error = await response.json();
        const errorMsg = error.detail || error.message || 'Errore durante il salvataggio';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Volontario save error:', error);
      if (error.message === 'Failed to fetch') {
        toast.error('Impossibile connettersi al server. Verifica la connessione internet.');
      } else {
        toast.error(`Errore: ${error.message || 'Errore durante il salvataggio'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="volontario-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/volontari')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
        <div className="page-header mb-0">
          <h1 className="page-title">{isEdit ? 'Modifica Volontario' : 'Nuovo Volontario'}</h1>
          <p className="page-subtitle">
            {isEdit ? 'Modifica i dati del volontario' : 'Inserisci i dati del nuovo volontario'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Tipo Volontario */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 flex-wrap">
                <Label htmlFor="tipologia_volontario" className="font-semibold text-base whitespace-nowrap">
                  Tipo Volontario <span className="text-red-500">*</span>
                </Label>
                <div className="flex-1 min-w-[250px] max-w-[400px]">
                  <Select 
                    value={formData.tipologia_volontario} 
                    onValueChange={(v) => handleChange('tipologia_volontario', v)}
                  >
                    <SelectTrigger data-testid="select-tipologia">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non_occasionale">Stabile / Non Occasionale</SelectItem>
                      <SelectItem value="occasionale">Occasionale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-500">
                  Finalità gestionale, organizzativa e assicurativa
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Dati Anagrafici */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-[#0055BB]" />
                Dati Anagrafici
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  required
                  data-testid="input-nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => handleChange('cognome', e.target.value)}
                  required
                  data-testid="input-cognome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                <Input
                  id="codice_fiscale"
                  value={formData.codice_fiscale}
                  onChange={(e) => handleChange('codice_fiscale', e.target.value.toUpperCase())}
                  maxLength={16}
                  className="font-mono uppercase"
                  data-testid="input-cf"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_nascita">Data di Nascita</Label>
                <Input
                  id="data_nascita"
                  type="date"
                  value={formData.data_nascita}
                  onChange={(e) => handleChange('data_nascita', e.target.value)}
                  data-testid="input-data-nascita"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="luogo_nascita">Luogo di Nascita</Label>
                <Input
                  id="luogo_nascita"
                  value={formData.luogo_nascita}
                  onChange={(e) => handleChange('luogo_nascita', e.target.value)}
                  data-testid="input-luogo-nascita"
                />
              </div>
            </CardContent>
          </Card>

          {/* Documento di Identità */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-[#0055BB]" />
                Documento di Identità
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tipo_documento_identita">Tipo Documento</Label>
                <Select
                  value={formData.tipo_documento_identita || ''}
                  onValueChange={(value) => handleChange('tipo_documento_identita', value)}
                >
                  <SelectTrigger data-testid="select-tipo-doc">
                    <SelectValue placeholder="Seleziona tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carta_identita">Carta d'Identità</SelectItem>
                    <SelectItem value="patente">Patente di Guida</SelectItem>
                    <SelectItem value="passaporto">Passaporto</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="numero_documento_identita">Numero Documento</Label>
                <Input
                  id="numero_documento_identita"
                  value={formData.numero_documento_identita || ''}
                  onChange={(e) => handleChange('numero_documento_identita', e.target.value.toUpperCase())}
                  placeholder="Es. CA12345AB"
                  className="font-mono uppercase"
                  data-testid="input-numero-doc"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="documento_rilasciato_da">Rilasciato da</Label>
                <Input
                  id="documento_rilasciato_da"
                  value={formData.documento_rilasciato_da || ''}
                  onChange={(e) => handleChange('documento_rilasciato_da', e.target.value)}
                  placeholder="Es. Comune di Roma, Prefettura..."
                  data-testid="input-rilasciato-da"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="documento_data_rilascio">Data Rilascio</Label>
                <Input
                  id="documento_data_rilascio"
                  type="date"
                  value={formData.documento_data_rilascio || ''}
                  onChange={(e) => handleChange('documento_data_rilascio', e.target.value)}
                  data-testid="input-data-rilascio"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="documento_data_scadenza">Data Scadenza</Label>
                <Input
                  id="documento_data_scadenza"
                  type="date"
                  value={formData.documento_data_scadenza || ''}
                  onChange={(e) => handleChange('documento_data_scadenza', e.target.value)}
                  data-testid="input-data-scadenza-doc"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Riceverai una notifica automatica quando il documento sta per scadere
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contatti */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Phone className="w-5 h-5 text-[#0055BB]" />
                Contatti
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="indirizzo">Indirizzo</Label>
                <Input
                  id="indirizzo"
                  value={formData.indirizzo}
                  onChange={(e) => handleChange('indirizzo', e.target.value)}
                  placeholder="Via, numero civico, CAP, città"
                  data-testid="input-indirizzo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Telefono</Label>
                <Input
                  id="telefono"
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  data-testid="input-telefono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pec">PEC</Label>
                <Input
                  id="pec"
                  type="email"
                  value={formData.pec}
                  onChange={(e) => handleChange('pec', e.target.value)}
                  data-testid="input-pec"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contatto_emergenza">Contatto di Emergenza</Label>
                <Input
                  id="contatto_emergenza"
                  value={formData.contatto_emergenza}
                  onChange={(e) => handleChange('contatto_emergenza', e.target.value)}
                  placeholder="Nome e numero"
                  data-testid="input-emergenza"
                />
              </div>
            </CardContent>
          </Card>

          {/* Attività */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-[#0055BB]" />
                Attività
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Stato e Date */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="stato">Stato</Label>
                  <Select value={formData.stato} onValueChange={(v) => handleChange('stato', v)}>
                    <SelectTrigger data-testid="select-stato">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qualifica">Qualifica / Ruolo</Label>
                  <Input
                    id="qualifica"
                    value={formData.qualifica}
                    onChange={(e) => handleChange('qualifica', e.target.value)}
                    placeholder="Es: Volontario, Coordinatore, ecc."
                    data-testid="input-qualifica"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_inizio_attivita">Data Inizio Attività</Label>
                  <Input
                    id="data_inizio_attivita"
                    type="date"
                    value={formData.data_inizio_attivita}
                    onChange={(e) => handleChange('data_inizio_attivita', e.target.value)}
                    data-testid="input-data-inizio"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_cessazione_attivita">Data Cessazione (se applicabile)</Label>
                  <Input
                    id="data_cessazione_attivita"
                    type="date"
                    value={formData.data_cessazione_attivita}
                    onChange={(e) => handleChange('data_cessazione_attivita', e.target.value)}
                    data-testid="input-data-fine"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="competenze">Competenze</Label>
                  <Input
                    id="competenze"
                    value={formData.competenze}
                    onChange={(e) => handleChange('competenze', e.target.value)}
                    placeholder="Descrivi le competenze del volontario"
                    data-testid="input-competenze"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="disponibilita">Disponibilità</Label>
                  <Input
                    id="disponibilita"
                    value={formData.disponibilita}
                    onChange={(e) => handleChange('disponibilita', e.target.value)}
                    placeholder="Es: Lunedì e mercoledì pomeriggio, weekend"
                    data-testid="input-disponibilita"
                  />
                </div>
              </div>

              {/* Campi specifici Volontario Non Occasionale */}
              {formData.tipologia_volontario === 'non_occasionale' && (
                <div className="grid gap-4 md:grid-cols-2 pt-4 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="presenza_registro_vidimato">Presenza nel Registro Vidimato</Label>
                    <Select 
                      value={formData.presenza_registro_vidimato} 
                      onValueChange={(v) => handleChange('presenza_registro_vidimato', v)}
                    >
                      <SelectTrigger data-testid="select-registro">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="si">Si</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="da_verificare">Da Verificare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="data_ultima_verifica_registro">Data Ultima Verifica</Label>
                    <Input
                      id="data_ultima_verifica_registro"
                      type="date"
                      value={formData.data_ultima_verifica_registro}
                      onChange={(e) => handleChange('data_ultima_verifica_registro', e.target.value)}
                      data-testid="input-data-verifica"
                    />
                  </div>
                </div>
              )}

              {/* Campi specifici Volontario Occasionale */}
              {formData.tipologia_volontario === 'occasionale' && (
                <div className="grid gap-4 md:grid-cols-2 pt-4 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="evento_riferimento_id">Evento di Riferimento</Label>
                    <Select 
                      value={formData.evento_riferimento_id} 
                      onValueChange={(v) => handleChange('evento_riferimento_id', v)}
                    >
                      <SelectTrigger data-testid="select-evento-rif">
                        <SelectValue placeholder="Seleziona evento..." />
                      </SelectTrigger>
                      <SelectContent>
                        {eventi.map(e => (
                          <SelectItem key={e.evento_id} value={e.evento_id}>
                            {e.titolo} ({e.data})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="periodo_attivita_occasionale">Periodo Attività</Label>
                    <Input
                      id="periodo_attivita_occasionale"
                      value={formData.periodo_attivita_occasionale}
                      onChange={(e) => handleChange('periodo_attivita_occasionale', e.target.value)}
                      placeholder="Es: 15-20 Marzo 2026"
                      data-testid="input-periodo-occ"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referente_occasionale">Referente</Label>
                    <Input
                      id="referente_occasionale"
                      value={formData.referente_occasionale}
                      onChange={(e) => handleChange('referente_occasionale', e.target.value)}
                      placeholder="Nome del referente"
                      data-testid="input-referente-occ"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="note_occasionale">Note</Label>
                    <Input
                      id="note_occasionale"
                      value={formData.note_occasionale}
                      onChange={(e) => handleChange('note_occasionale', e.target.value)}
                      placeholder="Note aggiuntive"
                      data-testid="input-note-occ"
                    />
                  </div>
                </div>
              )}

              {/* Motivazione modifica (solo in edit) */}
              {isEdit && (
                <div className="space-y-2 pt-4 border-t">
                  <Label htmlFor="motivazione_modifica">Motivazione Modifica Classificazione</Label>
                  <Textarea
                    id="motivazione_modifica"
                    value={formData.motivazione_modifica}
                    onChange={(e) => handleChange('motivazione_modifica', e.target.value)}
                    placeholder="Indicare la motivazione della modifica (obbligatoria per cambio classificazione)"
                    rows={2}
                    data-testid="input-motivazione"
                  />
                  <p className="text-xs text-slate-500">
                    Le modifiche alla classificazione vengono tracciate nell'audit trail
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Note e Privacy */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-[#0055BB]" />
                Note e Privacy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="note">Note Interne</Label>
                <Textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) => handleChange('note', e.target.value)}
                  placeholder="Note interne sul volontario"
                  rows={3}
                  data-testid="input-note"
                />
              </div>
              <div className="flex items-start space-x-3 pt-2">
                <Checkbox
                  id="consenso_privacy"
                  checked={formData.consenso_privacy}
                  onCheckedChange={(checked) => handleChange('consenso_privacy', checked)}
                  data-testid="checkbox-privacy"
                />
                <div className="space-y-1">
                  <Label htmlFor="consenso_privacy" className="cursor-pointer">
                    Consenso Privacy
                  </Label>
                  <p className="text-sm text-slate-500">
                    Il volontario ha fornito il consenso al trattamento dei dati personali
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/volontari')}>
              Annulla
            </Button>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-btn">
              {saving ? <span className="spinner mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {isEdit ? 'Salva Modifiche' : 'Crea Volontario'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
